import { ReactNode } from 'react'
import { alpha, Box, SxProps, Theme } from '@mui/material'

export const drawingToolbarSelectedBg = (theme: Theme) =>
    alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)

export const DRAWING_TOOLBAR_ICON_SIZE = 28
export const DRAWING_TOOLBAR_CHEVRON_HEIGHT = 12

export const DRAWING_TOOLBAR_CHEVRON_CLASS = 'drawing-toolbar-chevron'
export const DRAWING_TOOLBAR_MENU_OPEN_CLASS = 'drawing-toolbar-menu-open'

const chevronShowSx: SxProps<Theme> = {
    opacity: 1,
    pointerEvents: 'auto'
}

const chevronHideSx: SxProps<Theme> = {
    opacity: 0,
    pointerEvents: 'none'
}

/**
 * Wraps the tool button + chevron. ::before extends the hover hit area above the button
 * without shifting the chevron upward.
 */
export const drawingToolbarToolSlotSx: SxProps<Theme> = {
    position: 'relative',
    display: 'inline-flex',
    flexShrink: 0,
    verticalAlign: 'top',
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: '100%',
        height: DRAWING_TOOLBAR_CHEVRON_HEIGHT
    },
    [`& .${DRAWING_TOOLBAR_CHEVRON_CLASS}`]: {
        ...chevronHideSx,
        transition: (theme) =>
            theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest })
    },
    [`&:hover .${DRAWING_TOOLBAR_CHEVRON_CLASS}, &:focus-within .${DRAWING_TOOLBAR_CHEVRON_CLASS}, &.${DRAWING_TOOLBAR_MENU_OPEN_CLASS} .${DRAWING_TOOLBAR_CHEVRON_CLASS}`]:
        chevronShowSx
}

export const drawingToolbarIconBoxSx = {
    width: DRAWING_TOOLBAR_ICON_SIZE,
    height: DRAWING_TOOLBAR_ICON_SIZE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
        width: DRAWING_TOOLBAR_ICON_SIZE,
        height: DRAWING_TOOLBAR_ICON_SIZE
    },
    '& .MuiSvgIcon-root': {
        fontSize: DRAWING_TOOLBAR_ICON_SIZE
    }
} as const

export const drawingToolbarButtonSx: SxProps<Theme> = {
    p: 0.25,
    minWidth: 32,
    height: 32
}

export const drawingToolbarMenuGroupSx: SxProps<Theme> = {
    position: 'relative',
    flexShrink: 0,
    alignItems: 'center',
    overflow: 'visible'
}

export const drawingToolbarChevronButtonSx = (menuOpen: boolean): SxProps<Theme> => ({
    position: 'absolute',
    bottom: 'calc(100% - 1px)',
    left: 0,
    right: 0,
    width: '100%',
    height: DRAWING_TOOLBAR_CHEVRON_HEIGHT,
    minWidth: 'unset',
    p: 0,
    m: 0,
    lineHeight: 0,
    zIndex: (theme) => theme.zIndex.tooltip,
    borderTopLeftRadius: (theme) => theme.shape.borderRadius,
    borderTopRightRadius: (theme) => theme.shape.borderRadius,
    justifyContent: 'center',
    bgcolor: (theme) => (menuOpen ? drawingToolbarSelectedBg(theme) : 'transparent'),
    color: (theme) => (menuOpen ? theme.palette.primary.main : theme.palette.text.secondary),
    transition: (theme) =>
        theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shortest
        }),
    '&:hover': {
        bgcolor: (theme) => drawingToolbarSelectedBg(theme),
        color: (theme) => theme.palette.primary.main
    }
})

type DrawingToolbarIconProps = {
    children: ReactNode
}

export const DrawingToolbarIcon = ({ children }: DrawingToolbarIconProps) => (
    <Box sx={drawingToolbarIconBoxSx}>{children}</Box>
)
